import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ManualPromise } from '@local/common';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { getModifiers, KeyName } from '@local/ts-infra';
import { GridStack, GridStackNode, GridStackWidget } from 'gridstack';
import {
  NgGridStackWidget,
  elementCB,
  nodesCB,
  NgGridStackOptions,
  GridstackComponent,
  gsCreateNgComponents,
} from 'gridstack/dist/angular';
import { cloneDeep, isEqual } from 'lodash';
import { WidgetsService } from '../../services/widgets.service';
import { ActionsWidgetComponent } from '../actions-widget/actions-widget.component';
import { CalendarWidgetComponent } from '../calendar-widget/calendar-widget.component';
import { CollectionWidgetComponent } from '../collection-widget/collection-widget.component';
import { FavoritesWidgetComponent } from '../favorites-widget/favorites-widget.component';
import { MailWidgetComponent } from '../mail-widget/mail-widget.component';
import { PostWidgetComponent } from '../post-widget/post-widget.component';
import { RecentsWidgetComponent } from '../recents-widget/recents-widget.component';
import { SharedWithMeComponent } from '../shared-with-me/shared-with-me.component';
import { WorkingOnComponent } from '../working-on/working-on.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HomeComponentFocused, HomePageService } from 'src/app/bar/services/home-page.service';
import { stopEvent } from '@shared/utils/elements-util';
import { HeaderWidgetComponent } from '../header-widget/header-widget.component';

export type GridSelectorType =
  | 'actions-widget'
  | 'calendar-widget'
  | 'collection-widget'
  | 'favorites-widget'
  | 'header-widget'
  | 'mail-widget'
  | 'post-widget'
  | 'recents-widget'
  | 'shared-with-me'
  | 'working-on';

export interface GridLayoutSettings {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minH?: number;
  minW?: number;
}
export interface GridWidgetSettings extends GridLayoutSettings {
  noResize?: boolean;
  noMove?: boolean;
  selector: GridSelectorType;
  input: any;
}
export type GridChanges = GridLayoutSettings[];
export interface GridModel {
  id: string;
  minRow: number;
  cellHeight: number;
  column?: number;
  minSaveWidth?: number;
  settings: GridWidgetSettings[];
}
@UntilDestroy()
@Component({
  selector: 'grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() set model(val: GridModel) {
    if (isEqual(val, this._model)) {
      return;
    }
    const idChanged = this._model?.id !== val?.id;
    this.changeId++;
    this._model = cloneDeep(val);
    if (idChanged) {
      this.gridOptions = null;
      this.items = [];
      setTimeout(() => {
        this.updateModel(true, this.changeId);
      }, 0);
      return;
    }
    this.updateModel(false, this.changeId);
  }

  @Output() onChange = new EventEmitter<GridChanges>();
  @Output() onResize = new EventEmitter<GridLayoutSettings>();
  @Output() focusWidgetChanged = new EventEmitter<string>();

  items: NgGridStackWidget[] = [];
  gridOptions: NgGridStackOptions;

  get model() {
    return this._model;
  }

  private _model: GridModel;
  private afterViewInitPromise = new ManualPromise<void>();
  private updatingModel: boolean;
  private changeId = 0;
  private componentFocused: boolean;
  private keyHandlerId: string;
  private selectedIndex: number;
  private focusedWidget: GridStackNode;

  private readonly COMPONENT_FOCUSED_ID = HomeComponentFocused.WIDGETS;

  @ViewChild(GridstackComponent) gridComp: GridstackComponent;

  constructor(
    private cdr: ChangeDetectorRef,
    private keyboardService: KeyboardService,
    private widgetsService: WidgetsService,
    private homePageService: HomePageService
  ) {
    const array = [
      ActionsWidgetComponent,
      CalendarWidgetComponent,
      CollectionWidgetComponent,
      FavoritesWidgetComponent,
      HeaderWidgetComponent,
      MailWidgetComponent,
      PostWidgetComponent,
      RecentsWidgetComponent,
      SharedWithMeComponent,
      WorkingOnComponent,
    ];
    GridstackComponent.addComponentToSelectorType(array);
    GridStack.addRemoveCB = gsCreateNgComponents;
  }

  ngOnInit() {
    this.registerKeyboardHandler();
    this.widgetsService.triggerWidgetFocus$.pipe(untilDestroyed(this)).subscribe((id: string) => this.focusWidget(id));
    this.homePageService.triggerComponentFocus$.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.live === this.COMPONENT_FOCUSED_ID) {
        this.componentFocused = true;
        this.mark(value.step === 'next' ? 0 : this._model.settings?.length - 1);
      } else {
        this.componentFocused = false;
        this.mark(null);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  private registerKeyboardHandler() {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 7);
  }

  ngAfterViewInit(): void {
    this.afterViewInitPromise.resolve();
  }

  private initGrid() {
    this.gridOptions = {
      margin: 8,
      column: this.model.column || 12,
      float: false,
      minRow: this.model.minRow,
      cellHeight: this.model.cellHeight,
      cellHeightUnit: 'px',
      marginUnit: 'px',
      children: [],
      resizable: { handles: 'all' },
      draggable: { handle: '.widget-handler' },
    };
  }

  private async updateModel(init: boolean, changeId: number) {
    await this.afterViewInitPromise;
    if (this.changeId !== changeId) {
      return;
    }
    this.updatingModel = true;
    if (init || !this.gridOptions) {
      this.initGrid();
    }
    if (!this.items.length) {
      this.items = cloneDeep(this.model?.settings);
      this.gridOptions.children = cloneDeep(this.model?.settings);
      this.updatingModel = false;
      this.cdr.markForCheck();
      return;
    }
    const newItems = this.model?.settings || [];
    const oldWidgets = cloneDeep(this.items);
    const oldIds = new Set(oldWidgets.map((a) => a.id));
    for (const item of newItems) {
      if (oldIds.has(item.id)) {
        continue;
      }
      this.gridComp?.grid?.addWidget(<any>item);
      this.items.push(cloneDeep(item));
    }
    const newIds = new Set(newItems.map((i) => i.id));
    for (let index = 0; index < oldWidgets.length; index++) {
      const item = oldWidgets[index];
      const id: string = item.id as string;
      const node = this.gridComp?.grid.engine.nodes.find((n) => n.id === id);
      if (!node) {
        continue;
      }
      if (newIds.has(id)) {
        const newIndex = newItems.findIndex((i) => i.id === id);
        const newItem = newItems[newIndex];
        const newItemW = { ...newItem, input: null };
        const oldItemW = { ...oldWidgets[index], input: null };
        if (isEqual(newItemW, oldItemW)) {
          continue;
        }
        const changeIndex = this.items.findIndex((i) => i.id === item.id);
        this.items[changeIndex] = cloneDeep(newItem);
        this.gridComp?.grid?.update(node.el, this.items[changeIndex]);
        continue;
      }
      this.items.splice(index, 1);
      this.gridComp?.grid?.removeWidget(node.el, true, false);
    }
    this.updatingModel = false;
    this.cdr.markForCheck();
  }

  onGridChange(data: nodesCB) {
    const width = this.gridComp.el.getBoundingClientRect().width;
    if (this.updatingModel || (this.model.minSaveWidth && width <= this.model.minSaveWidth) || !data.nodes) {
      return;
    }
    const changes: GridChanges = [];
    for (const node of data.nodes) {
      changes.push({ id: <string>node.id, x: node.x, y: node.y, w: node.w, h: node.h });
    }
    this.onChange.emit(changes);
  }

  private mark(index: number) {
    const widgets = this._model.settings?.sort((a, b) => {
      if (b?.y === a?.y) {
        return a?.x - b?.x;
      }
      return a?.y - b?.y;
    });
    this.selectedIndex = index;
    const widgetId = widgets[index]?.id;
    this.focusWidget(widgetId);
    this.focusWidgetChanged.emit(widgetId);
  }

  private focusWidget(id: string) {
    if (id === this.focusedWidget?.id) return;
    this.focusedWidget?.el?.classList.remove('focused');
    this.focusedWidget = this.gridComp?.grid.engine.nodes.find((n) => n.id === id);
    this.focusedWidget?.el.classList.add('focused');
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.componentFocused) {
      return;
    }
    const modifiers = getModifiers(keys);
    const key = keys[0];

    if (key === 'tab') {
      stopEvent(event);
      const lastIndex = this._model.settings.length - 1;
      if (!this.selectedIndex && this.selectedIndex !== 0) {
        this.mark(0);
        return;
      }

      if (modifiers.length === 1 && modifiers[0] === 'shift') {
        if (this.selectedIndex === 0) {
          this.homePageService.keyboardComponentFocus$.next({
            step: 'prev',
            live: this.COMPONENT_FOCUSED_ID,
          });
          return;
        }
        this.mark(this.selectedIndex - 1);
        return;
      }

      if (this.selectedIndex === lastIndex) {
        this.homePageService.keyboardComponentFocus$.next({
          step: 'next',
          live: this.COMPONENT_FOCUSED_ID,
        });
        return;
      }

      this.mark(++this.selectedIndex);
    }
  }

  onGridResize(data: elementCB) {
    if (this.updatingModel) {
      return;
    }
    const node = data.el.gridstackNode;
    const id = node.id as string;
    const settings: GridLayoutSettings = { id, x: node.x, y: node.y, w: node.w, h: node.h };
    this.onResize.emit(settings);
  }

  identify(index: number, w: GridStackWidget) {
    return w.id;
  }
}
